import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bold, 
  Italic, 
  List, 
  Link as LinkIcon, 
  Type, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Save,
  FileText, 
  PlusCircle, 
  Eye, 
  Palette,
  X,
  Upload,
  Loader
} from 'lucide-react';
import { marked } from 'marked';
// Update this import path to match your project structure
import ImageUpload from '../components/ImageUpload';  // Adjust this path
import DOMPurify from 'dompurify';
import Markdown from 'markdown-to-jsx';

const ToolbarButton = ({ icon: Icon, active, onClick, tooltip, color }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded hover:bg-gray-100 ${
      active ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
    }`}
    title={tooltip}
    style={color ? { color } : undefined}
  >
    <Icon size={18} />
  </button>
);

const ColorPicker = ({ onSelect, onClose }) => {
  const colors = [
    { name: 'Default', value: 'inherit' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Gray', value: '#6b7280' }
  ];

  return (
    <div className="absolute top-full mt-2 p-2 bg-white rounded-lg shadow-xl border grid grid-cols-3 gap-1 z-50">
      {colors.map(color => (
        <button
          key={color.value}
          onClick={() => {
            onSelect(color.value);
            onClose();
          }}
          className="w-8 h-8 rounded hover:ring-2 ring-offset-2 flex items-center justify-center"
          style={{ 
            backgroundColor: color.value === 'inherit' ? '#fff' : color.value,
            border: color.value === 'inherit' ? '1px solid #e5e7eb' : 'none'
          }}
          title={color.name}
        >
          {color.value === 'inherit' && <span className="text-sm">A</span>}
        </button>
      ))}
    </div>
  );
};

const NewsletterEditor = () => {
    const { id } = useParams(); // Get newsletter ID from URL if editing
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [newsletter, setNewsletter] = useState({
      title: '',
      subtitle: '',
      content: '',
      category: 'general',
      tags: [],
      status: 'draft',
      coverImage: ''
    });
    const renderPreview = useCallback((content) => {
        if (!content) return '<p style="color: #9CA3AF">No content yet</p>';
        
        try {
          // Add basic styles to the renderer
          const renderer = {
            heading(text, level) {
              const sizes = {
                1: 'style="font-size: 2.25rem; font-weight: bold; margin-bottom: 1.5rem;"',
                2: 'style="font-size: 1.875rem; font-weight: bold; margin-bottom: 1.25rem;"',
                3: 'style="font-size: 1.5rem; font-weight: bold; margin-bottom: 1rem;"',
                4: 'style="font-size: 1.25rem; font-weight: bold; margin-bottom: 0.75rem;"',
                5: 'style="font-size: 1.125rem; font-weight: bold; margin-bottom: 0.5rem;"',
                6: 'style="font-size: 1rem; font-weight: bold; margin-bottom: 0.5rem;"'
              };
              return `<h${level} ${sizes[level]}>${text}</h${level}>`;
            },
            paragraph(text) {
              return `<p style="color: #374151; line-height: 1.625; margin-bottom: 1rem;">${text}</p>`;
            },
            list(body, ordered) {
              const style = 'style="padding-left: 1.25rem; margin-bottom: 1rem; list-style-type: ' + 
                (ordered ? 'decimal' : 'disc') + ';"';
              return `<${ordered ? 'ol' : 'ul'} ${style}>${body}</${ordered ? 'ol' : 'ul'}>`;
            },
            listitem(text) {
              return `<li style="margin-left: 1rem; margin-bottom: 0.5rem;">${text}</li>`;
            },
            link(href, title, text) {
              return `<a href="${href}" style="color: #2563EB; text-decoration: underline;" title="${title || ''}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            },
            image(href, title, text) {
              return `<img src="${href}" alt="${text}" title="${title || ''}" style="border-radius: 0.5rem; margin: 1rem 0; max-width: 100%;" />`;
            }
          };
      
          // Configure marked with the custom renderer
          marked.use({ renderer });
      
          // Convert markdown to HTML
          const rawHtml = marked.parse(content);
          
          // Sanitize the HTML
          const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
            ADD_TAGS: ['div', 'span'],
            ADD_ATTR: ['style']
          });
          
          return sanitizedHtml;
        } catch (error) {
          console.error('Preview rendering error:', error);
          return '<p style="color: #EF4444;">Error rendering preview</p>';
        }
      }, []);
      const handleToolbarAction = useCallback((type, value) => {
        const textarea = document.getElementById('content-editor');
        if (!textarea) return;
    
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = newsletter.content.slice(start, end);
        let insertion = '';
    
        switch (type) {
          case 'h1':
            insertion = `\n# ${selected || 'Heading 1'}\n`;
            break;
          case 'h2':
            insertion = `\n## ${selected || 'Heading 2'}\n`;
            break;
          case 'bold':
            insertion = `**${selected || 'bold text'}**`;
            break;
          case 'italic':
            insertion = `*${selected || 'italic text'}*`;
            break;
          case 'list':
            insertion = selected
              ? selected.split('\n').map(line => `- ${line}`).join('\n')
              : '\n- List item\n';
            break;
          case 'link':
            insertion = `[${selected || 'link text'}](url)`;
            break;
          case 'color':
            insertion = `<span style="color: ${value}">${selected || 'colored text'}</span>`;
            break;
          case 'alignLeft':
            insertion = `<div style="text-align: left">\n${selected || 'left aligned text'}\n</div>`;
            break;
          case 'alignCenter':
            insertion = `<div style="text-align: center">\n${selected || 'centered text'}\n</div>`;
            break;
          case 'alignRight':
            insertion = `<div style="text-align: right">\n${selected || 'right aligned text'}\n</div>`;
            break;
          default:
            return;
        }
    
        const newContent = newsletter.content.slice(0, start) + insertion + newsletter.content.slice(end);
        setNewsletter(prev => ({ ...prev, content: newContent }));
        
        // Set cursor position after insertion
        setTimeout(() => {
          textarea.focus();
          const newPosition = start + insertion.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }, [newsletter.content]);
    useEffect(() => {
        const fetchNewsletter = async () => {
          if (!id) return;
    
          setInitialLoading(true);
          try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/newsletter/${id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            });
    
            if (!response.ok) {
              throw new Error('Newsletter not found');
            }
    
            const data = await response.json();
            setNewsletter({
              title: data.title,
              subtitle: data.subtitle || '',
              content: data.content,
              category: data.category,
              tags: data.tags || [],
              status: data.status,
              coverImage: data.coverImage || ''
            });
          } catch (error) {
            console.error('Error fetching newsletter:', error);
            navigate('/newsletters'); // Redirect back to list on error
          } finally {
            setInitialLoading(false);
          }
        };
    
        fetchNewsletter();
      }, [id, navigate]);
    
      const saveNewsletter = async (status) => {
        setLoading(true);
        try {
          const url = id
            ? `${process.env.REACT_APP_BASE_URL}/api/newsletter/${id}`
            : `${process.env.REACT_APP_BASE_URL}/api/newsletter`;
          
          const method = id ? 'PUT' : 'POST';
    
          const response = await fetch(url, {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ ...newsletter, status })
          });
    
          if (!response.ok) throw new Error('Failed to save newsletter');
          
          const savedNewsletter = await response.json();
          navigate('/newsletters'); // Redirect to list after successful save
        } catch (error) {
          console.error('Error saving newsletter:', error);
        } finally {
          setLoading(false);
        }
      };
      if (initialLoading) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        );
      }

  
  // Update the preview container in your JSX:
  {/* Content Area */}
{/* Content Area */}
{previewMode ? (
    <div className="p-6">
      {/* Editor Preview */}
      <div className="prose max-w-none">
        <Markdown
          options={{
            overrides: {
              p: {
                component: props => <p className="text-gray-800 mb-4 leading-relaxed" {...props} />
              },
              strong: {
                component: props => <strong className="font-bold" {...props} />
              },
              em: {
                component: props => <em className="italic" {...props} />
              },
              ul: {
                component: props => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />
              },
              ol: {
                component: props => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />
              },
              li: {
                component: props => <li className="text-gray-800" {...props} />
              },
              h1: {
                component: props => <h1 className="text-4xl font-bold mb-6" {...props} />
              },
              h2: {
                component: props => <h2 className="text-3xl font-bold mb-5" {...props} />
              },
              h3: {
                component: props => <h3 className="text-2xl font-bold mb-4" {...props} />
              },
              h4: {
                component: props => <h4 className="text-xl font-bold mb-3" {...props} />
              },
              a: {
                component: props => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />
              },
              span: {
                component: 'span'  // This preserves styled spans
              },
              div: {
                component: 'div'   // This preserves styled divs for alignment
              }
            },
            forceBlock: true,
            forceWrapper: true,
            wrapper: props => <div className="text-gray-800" {...props} />
          }}
        >
          {newsletter.content || ''}
        </Markdown>
      </div>
    </div>
  ) : (
    <textarea
      id="content-editor"
      value={newsletter.content}
      onChange={(e) => setNewsletter(prev => ({ ...prev, content: e.target.value }))}
      placeholder="Start writing your newsletter..."
      className="w-full h-[500px] p-6 border-0 focus:ring-0 resize-none font-mono"
    />
  )}



  const handleImageUpload = (url) => {
    setNewsletter(prev => ({ ...prev, coverImage: url }));
  };

 

  const previewContent = newsletter.content
    ? marked(newsletter.content)
    : '<p class="text-gray-400">No content yet</p>';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Newsletter' : 'Create Newsletter'}
            </h1>
            <p className="text-gray-600">Share updates with your alumni community</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={() => saveNewsletter('draft')}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </button>
            <button
              onClick={() => saveNewsletter('published')}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              {id ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Title Section */}
          <div className="p-6 border-b border-gray-200">
            <input
              type="text"
              placeholder="Newsletter Title"
              value={newsletter.title}
              onChange={(e) => setNewsletter(prev => ({ ...prev, title: e.target.value }))}
              className="w-full text-3xl font-bold border-0 focus:ring-0 p-0 mb-4"
            />
            <input
              type="text"
              placeholder="Subtitle (optional)"
              value={newsletter.subtitle}
              onChange={(e) => setNewsletter(prev => ({ ...prev, subtitle: e.target.value }))}
              className="w-full text-lg text-gray-600 border-0 focus:ring-0 p-0"
            />
          </div>

          {/* Cover Image */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <ImageUpload
              onImageUpload={handleImageUpload}
              existingImage={newsletter.coverImage}
              className="max-w-xl mx-auto"
            />
          </div>

          {/* Toolbar */}
          <div className="p-2 border-b border-gray-200 flex flex-wrap gap-1">
            <ToolbarButton icon={Type} onClick={() => handleToolbarAction('h1')} tooltip="Heading 1" />
            <ToolbarButton icon={Type} onClick={() => handleToolbarAction('h2')} tooltip="Heading 2" />
            <ToolbarButton icon={Bold} onClick={() => handleToolbarAction('bold')} tooltip="Bold" />
            <ToolbarButton icon={Italic} onClick={() => handleToolbarAction('italic')} tooltip="Italic" />
            <ToolbarButton icon={List} onClick={() => handleToolbarAction('list')} tooltip="List" />
            <ToolbarButton icon={LinkIcon} onClick={() => handleToolbarAction('link')} tooltip="Link" />
            <div className="relative">
              <ToolbarButton 
                icon={Palette} 
                onClick={() => setShowColorPicker(!showColorPicker)}
                tooltip="Text Color"
              />
              {showColorPicker && (
                <ColorPicker 
                  onSelect={(color) => handleToolbarAction('color', color)}
                  onClose={() => setShowColorPicker(false)}
                />
              )}
            </div>
            <div className="h-6 w-px bg-gray-200 mx-2" />
            <ToolbarButton icon={AlignLeft} onClick={() => handleToolbarAction('alignLeft')} tooltip="Align Left" />
            <ToolbarButton icon={AlignCenter} onClick={() => handleToolbarAction('alignCenter')} tooltip="Align Center" />
            <ToolbarButton icon={AlignRight} onClick={() => handleToolbarAction('alignRight')} tooltip="Align Right" />
          </div>

          {/* Content Area */}
{/* Content Area */}
{previewMode ? (
  <div className="p-6">
    <div className="prose max-w-none">
      <Markdown
        options={{
          overrides: {
            p: {
              component: props => <p className="text-gray-800 mb-4 leading-relaxed" {...props} />
            },
            strong: {
              component: props => <strong className="font-bold" {...props} />
            },
            em: {
              component: props => <em className="italic" {...props} />
            },
            ul: {
              component: props => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />
            },
            ol: {
              component: props => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />
            },
            li: {
              component: props => <li className="text-gray-800" {...props} />
            },
            h1: {
              component: props => <h1 className="text-4xl font-bold mb-6" {...props} />
            },
            h2: {
              component: props => <h2 className="text-3xl font-bold mb-5" {...props} />
            },
            h3: {
              component: props => <h3 className="text-2xl font-bold mb-4" {...props} />
            },
            h4: {
              component: props => <h4 className="text-xl font-bold mb-3" {...props} />
            },
            a: {
              component: props => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />
            },
            span: {
              component: 'span'
            },
            div: {
              component: 'div'
            }
          },
          forceBlock: true,
          forceWrapper: true,
          wrapper: props => <div className="text-gray-800" {...props} />
        }}
      >
        {newsletter.content || ''}
      </Markdown>
    </div>
  </div>
) : (
  <textarea
    id="content-editor"
    value={newsletter.content}
    onChange={(e) => setNewsletter(prev => ({ ...prev, content: e.target.value }))}
    placeholder="Start writing your newsletter..."
    className="w-full h-[500px] p-6 border-0 focus:ring-0 resize-none font-mono"
  />
)}

          {/* Metadata Section */}
          <div className="border-t border-gray-200 p-6 bg-gray-50 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newsletter.category}
                  onChange={(e) => setNewsletter(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="general">General</option>
                  <option value="announcement">Announcement</option>
                  <option value="event">Event</option>
                  <option value="achievement">Achievement</option>
                  <option value="department">Department</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  placeholder="Enter tags separated by commas"
                  value={newsletter.tags.join(', ')}
                  onChange={(e) => setNewsletter(prev => ({
                    ...prev,
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={() => saveNewsletter('draft')}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Save as Draft
          </button>
          <button
            onClick={() => saveNewsletter('published')}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Publishing...
                </span>
              ) : (
                'Publish Newsletter'
              )}
            </button>
          </div>
        </div>
  
   
{/* Preview Modal */}
{previewMode && (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Preview Newsletter</h2>
        <button
          onClick={() => setPreviewMode(false)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="p-6">
        {newsletter.title && (
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {newsletter.title}
          </h1>
        )}
        
        {newsletter.subtitle && (
          <h2 className="text-xl text-gray-600 mb-6">
            {newsletter.subtitle}
          </h2>
        )}

        {newsletter.coverImage && (
          <img
            src={newsletter.coverImage}
            alt={newsletter.title}
            className="w-full h-64 object-cover rounded-lg mb-8"
          />
        )}

        {/* Updated Content Preview */}
        <div className="prose max-w-none">
          <Markdown
            options={{
              overrides: {
                p: {
                  component: props => <p className="text-gray-800 mb-4 leading-relaxed" {...props} />
                },
                strong: {
                  component: props => <strong className="font-bold" {...props} />
                },
                em: {
                  component: props => <em className="italic" {...props} />
                },
                ul: {
                  component: props => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />
                },
                ol: {
                  component: props => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />
                },
                li: {
                  component: props => <li className="text-gray-800" {...props} />
                },
                h1: {
                  component: props => <h1 className="text-4xl font-bold mb-6" {...props} />
                },
                h2: {
                  component: props => <h2 className="text-3xl font-bold mb-5" {...props} />
                },
                h3: {
                  component: props => <h3 className="text-2xl font-bold mb-4" {...props} />
                },
                h4: {
                  component: props => <h4 className="text-xl font-bold mb-3" {...props} />
                },
                a: {
                  component: props => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />
                },
                span: {
                  component: 'span'
                },
                div: {
                  component: 'div'
                }
              },
              forceBlock: true,
              forceWrapper: true,
              wrapper: props => <div className="text-gray-800" {...props} />
            }}
          >
            {newsletter.content || ''}
          </Markdown>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
              {newsletter.category}
            </span>
            {newsletter.tags.map((tag, index) => (
              <span 
                key={index} 
                className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-4">
        <button
          onClick={() => setPreviewMode(false)}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          Continue Editing
        </button>
        <button
          onClick={() => saveNewsletter('published')}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
        >
          {loading ? 'Publishing...' : 'Publish Now'}
        </button>
      </div>
    </div>
  </div>
)}

   

      </div>
    );
  };
  
  export default NewsletterEditor;